import { useState } from 'react';
import type { Invoice } from '@repo/shared-types';
import { InvoiceStatusBadge } from '../../lib/invoiceStatus';
import { Button } from '@repo/ui/src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/src/components/ui/card';
import { Separator } from '@repo/ui/src/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { DropdownMenuItem } from '@repo/ui/src/components/ui/dropdown-menu';
import { ArrowLeft, Bell, Copy, Edit, ExternalLink, Send, Sparkles, Trash, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { cn } from '@repo/ui/src/lib/utils';
import { hasNothingToCollect } from '@repo/shared-types';
import { useInvoiceQr, useDeleteInvoice, useInvoice } from '../../hooks/useInvoices';
import { RecordPaymentDrawer } from '../payments/components/RecordPaymentDrawer';
import { SendInvoiceDialog } from './components/SendInvoiceDialog';
import { ReviewTargetsCard } from './components/ReviewTargetsCard';
import { useAuthStore } from '../../stores/authStore';
import { PageHeader } from '../../components/PageHeader';

// Lifted so the public link built for `publicLink` below and the ⋮ menu's
// "Link công khai" item share the one literal instead of drifting apart.
const WEB_URL = import.meta.env.VITE_WEB_URL || 'http://localhost:5173';

// Section label: quiet uppercase ledger caption reused across the info panels.
function Label({ children }: { children: React.ReactNode }) {
  return (
    <CardTitle className="text-[11px] font-semibold uppercase tracking-wider text-surface-400">
      {children}
    </CardTitle>
  );
}

export function InvoiceDetailPage() {
  const { t } = useTranslation('invoices');
  const { id } = useParams();
  const invoiceId = id ?? null;
  const navigate = useNavigate();

  const { data: invoice, isLoading } = useInvoice(invoiceId);
  const { mutate: deleteInvoice, isPending: isDeleting } = useDeleteInvoice();
  // C1 fix: the public link stamps `publicViewedAt` and logs an INVOICE_VIEWED
  // activity as an unavoidable side effect of opening it (that route is
  // public and unauthenticated by design, so there is no guard to reach from
  // here). One click from an impersonating admin would permanently — and
  // falsely — tell the teacher a client viewed an invoice they never opened.
  // This is the one deliberate exception to "feature code never knows
  // impersonation exists": the knowledge stays confined to not rendering
  // this link, nothing else about invoice logic branches on it.
  const impersonatedBy = useAuthStore((s) => s.impersonatedBy);
  const [sendOpen, setSendOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Called above the early returns; `enabled` is what actually gates the
  // request, so nothing is fetched until the invoice has loaded and is
  // genuinely collectable. Desktop fetches it too (the block below is hidden by
  // CSS, not by a hook — useIsMobile would flash) — that is a deliberate trade:
  // one cached request that also pre-warms exactly what the send dialog opens
  // with, versus a first-paint flash of the wrong layout.
  const qrEnabled =
    !!invoice &&
    (invoice.status === 'DRAFT' || invoice.status === 'PENDING' || invoice.status === 'PARTIAL') &&
    !hasNothingToCollect(invoice);
  const { data: qr } = useInvoiceQr(invoiceId ?? '', qrEnabled);

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-4xl p-6 sm:p-8">
        <div className="h-4 w-24 animate-pulse rounded bg-surface-200" />
        <div className="mt-4 h-9 w-64 animate-pulse rounded bg-surface-200" />
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          <div className="h-40 animate-pulse rounded-md border border-surface-200 bg-surface-100/70 md:col-span-1" />
          <div className="h-64 animate-pulse rounded-md border border-surface-200 bg-surface-100/70 md:col-span-2" />
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="mx-auto w-full max-w-4xl p-6 sm:p-8">
        <h1 className="mb-4 text-2xl font-semibold text-surface-900">{t('detail.notFound')}</h1>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> {t('detail.backToList')}
        </Button>
      </div>
    );
  }

  const handleDelete = () => {
    deleteInvoice(invoice.id, {
      onSuccess: () => navigate('/invoices'),
    });
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);

  const getStatusBadge = (inv: Pick<Invoice, 'status' | 'isOverdue'>) => (
    <InvoiceStatusBadge status={inv.status} isOverdue={inv.isOverdue} />
  );

  const payable = invoice.status === 'DRAFT' || invoice.status === 'PENDING' || invoice.status === 'PARTIAL';
  const balanceDue = Number(invoice.totalAmount) - Number(invoice.depositAmount);

  const publicLink = `${WEB_URL}/invoice/p/${invoice.publicToken}`;
  const nothingToCollect = hasNothingToCollect(invoice);
  const isDraft = invoice.status === 'DRAFT';
  const hasDeposit = invoice.depositAmount > 0;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicLink);
      toast.success(t('sendDialog.zalo.copied'));
    } catch {
      toast.error(t('sendDialog.zalo.copyFailed'));
    }
  };

  // When the money landed, as a ledger row in the Chi tiết card — for BOTH
  // sources (the list's sparkles line covers only SePay; Johnny 2026-09-01).
  // `lastPayment.at` is a REAL instant (see InvoiceLastPayment's docblock):
  // format it in Asia/Ho_Chi_Minh explicitly, never the browser zone. SePay
  // carries a true time-of-day; a manual mark stores a date-only midnight
  // (RecordPaymentDrawer sends `T00:00:00.000Z`), so manual renders the date
  // alone — printing that fabricated midnight as a time would be a lie.
  const lastPaid = (() => {
    if (!invoice.lastPayment) return null;
    if (invoice.status !== 'PAID' && invoice.status !== 'PARTIAL') return null;
    const d = new Date(invoice.lastPayment.at);
    if (Number.isNaN(d.getTime())) return null;
    const isAuto = invoice.lastPayment.source === 'SEPAY';
    const date = d.toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
    const time = d.toLocaleTimeString('vi-VN', {
      timeZone: 'Asia/Ho_Chi_Minh',
      hour: '2-digit',
      minute: '2-digit',
    });
    return { isAuto, source: invoice.lastPayment.source, text: isAuto ? `${time} ${date}` : date };
  })();

  return (
    <div className="mx-auto w-full max-w-4xl p-6 sm:p-8 motion-safe:animate-[fade-up_0.4s_cubic-bezier(0.22,1,0.36,1)_both]">
      <PageHeader
        size="md"
        back={{ label: t('detail.backToList'), onClick: () => navigate(-1) }}
        title={t('detail.invoiceTitle', { code: invoice.invoiceCode })}
        badge={getStatusBadge(invoice)}
        actions={[
          {
            key: 'send',
            label: t('detail.sendInvoice'),
            icon: Send,
            onClick: () => setSendOpen(true),
            hidden: invoice.status !== 'DRAFT',
          },
          {
            key: 'reminder',
            label: t('detail.sendReminder'),
            icon: Bell,
            variant: 'outline',
            onClick: () => setReminderOpen(true),
            hidden: !(invoice.status === 'PENDING' || invoice.status === 'PARTIAL'),
          },
          {
            key: 'mark-paid',
            label: t('detail.markPaid'),
            icon: Wallet,
            // Recording payment is the key action once an invoice is out;
            // keep Send primary while DRAFT, then promote Mark-paid.
            variant: invoice.status === 'DRAFT' ? 'outline' : 'default',
            onClick: () => setRecordOpen(true),
            hidden: !payable,
          },
          {
            key: 'edit',
            label: t('detail.edit'),
            icon: Edit,
            // No FE gating here beyond DRAFT status — a non-Pro or
            // non-sellsExtras org may still open the editor and add items;
            // the PUT /:id 403 (FEATURE_LOCKED) + upgrade-dialog auto-open
            // guard the actual save. See useExtrasAccess / Task 3 Step 5.
            onClick: () => navigate(`/invoices/${invoice.id}/edit`),
            hidden: invoice.status !== 'DRAFT',
          },
          {
            key: 'delete',
            label: t('detail.delete'),
            icon: Trash,
            onClick: () => setConfirmDelete(true),
            disabled: isDeleting,
            destructive: true,
          },
        ]}
        // The public-link item is kept out of `actions` deliberately: it has no
        // status gating (always shown unless impersonating), so on PAID/
        // CANCELLED invoices — where Send/Reminder/Mark-paid/Edit are all
        // hidden — the split algorithm would promote it to an inline button
        // instead of leaving it in the ⋮ menu, which is a real behaviour
        // change from today. `overflow` keeps it pinned inside the menu
        // exactly as it renders now.
        overflow={
          impersonatedBy ? (
            <DropdownMenuItem disabled className="flex-col items-start gap-0.5 whitespace-normal py-2">
              <span className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4" /> {t('detail.publicLink')}
              </span>
              <span className="pl-6 text-[11px] leading-snug text-surface-400">
                {t('detail.publicLinkHiddenImpersonating')}
              </span>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem asChild>
              <a
                href={publicLink}
                target="_blank"
                rel="noreferrer"
                className="cursor-pointer"
              >
                <ExternalLink className="mr-2 h-4 w-4" /> {t('detail.publicLink')}
              </a>
            </DropdownMenuItem>
          )
        }
      />

      <div className="grid gap-6 md:grid-cols-3">
        {/* Info Column */}
        <div className="space-y-6 md:col-span-1">
      {/* The hand-the-screen-over card, every breakpoint (Johnny 2026-09-01:
          a parent standing in the room scans a laptop screen as readily as a
          phone). The amount owed and the code to scan come first; the send
          dialog still owns SENDING the QR elsewhere — this card and that
          dialog both derive from the same balanceDue, one component each, so
          they cannot drift apart. */}
      <div className="rounded-md border border-surface-200 bg-surface-0 p-4 text-center">
        <p className="text-[12px] text-surface-500">{payable ? t('detail.balanceDue') : t('detail.total')}</p>
        <p className="font-display tabular mt-0.5 text-[30px] font-semibold leading-none tracking-tight text-surface-900">
          {formatCurrency(payable ? balanceDue : invoice.totalAmount)}
        </p>

        {payable && !nothingToCollect ? (
          <>
            {/* 152px pinned: --spacing is 0.2rem, so w-38 would be 30.4px.
                A deposit invoice never gets the QR here: it's built from
                `invoice.totalAmount` (see GET /invoices/:id/qr), not the
                balance still owed, so scanning it would ask a parent to pay
                the whole thing twice. The note replaces both the QR and the
                consequence line below it — that sentence promises the QR's
                own auto-match behaviour, which doesn't apply to this amount. */}
            {hasDeposit ? (
              <p className="mx-auto mt-3 max-w-[220px] text-[12px] leading-relaxed text-surface-500">
                {t('detail.qrDepositNote')}
              </p>
            ) : qr?.qrUrl ? (
              <img
                src={qr.qrUrl}
                alt="VietQR"
                className="mx-auto mt-3 h-[152px] w-[152px] rounded-md border border-surface-200 bg-white"
              />
            ) : (
              <div className="mx-auto mt-3 h-[152px] w-[152px] rounded-md border border-dashed border-surface-200 bg-surface-50" />
            )}

            <div className="mt-3 flex gap-2">
              {!impersonatedBy && (
                <Button type="button" variant="outline" className="h-[44px] flex-1" onClick={handleCopyLink}>
                  <Copy className="mr-2 h-4 w-4" /> {t('detail.copyLink')}
                </Button>
              )}
              <Button
                type="button"
                className="h-[44px] flex-[1.3]"
                onClick={() => (isDraft ? setSendOpen(true) : setReminderOpen(true))}
              >
                <Send className="mr-2 h-4 w-4" />
                {isDraft ? t('detail.sendInvoice') : t('detail.sendReminder')}
              </Button>
            </div>

            {!hasDeposit && (
              <p className="mt-3 text-[12px] leading-relaxed text-surface-500">{t('detail.qrConsequence')}</p>
            )}
          </>
        ) : (
          // Settled (or nothing left to collect): the green state instead of a
          // QR nobody should scan. No new copy — `status.PAID` is the same
          // words the badge in the header uses. Cancelled gets a neutral
          // colour instead — there is nothing settled to celebrate about it.
          <p
            className={cn(
              'mt-2 text-[13px] font-medium',
              invoice.status === 'CANCELLED' ? 'text-surface-500' : 'text-brand-700',
            )}
          >
            {t(`status.${invoice.status}`)}
          </p>
        )}

      </div>
          <Card className="shadow-none">
            <CardHeader>
              <Label>{t('detail.client')}</Label>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold text-surface-900">{invoice.client?.name}</p>
              {invoice.client?.email && (
                <p className="mt-0.5 text-sm text-surface-500">{invoice.client.email}</p>
              )}
              {invoice.client?.zaloPhone && (
                <p className="tabular text-sm text-surface-500">{invoice.client.zaloPhone}</p>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader>
              <Label>{t('detail.details')}</Label>
            </CardHeader>
            <CardContent className="space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-surface-500">{t('detail.created')}</span>
                <span className="tabular text-surface-800">{new Date(invoice.createdAt).toLocaleDateString('vi-VN')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-surface-500">{t('detail.dueDate')}</span>
                <span className="tabular font-medium text-surface-900">
                  {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('vi-VN') : '—'}
                </span>
              </div>
              {invoice.publicViewedAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-surface-500">{t('detail.publicViewedAt')}</span>
                  <span className="tabular text-surface-800">
                    {new Date(invoice.publicViewedAt).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' })}
                  </span>
                </div>
              )}
              {lastPaid && (
                <div className="flex justify-between gap-3 text-sm">
                  <span className="shrink-0 whitespace-nowrap text-surface-500">
                    {t(invoice.status === 'PAID' ? 'detail.paidAt' : 'detail.lastPaymentAt')}
                  </span>
                  <span className="tabular text-right text-surface-800">
                    {lastPaid.text}
                    <span className="block text-[11px] font-normal text-brand-700">
                      {lastPaid.isAuto ? (
                        <>
                          <Sparkles className="mr-1 inline-block h-3 w-3 align-[-1px]" aria-hidden />
                          {t('detail.autoMatchedShort')}
                        </>
                      ) : (
                        `${t('detail.manualMarked')} · ${t(`detail.paySource.${lastPaid.source}`)}`
                      )}
                    </span>
                  </span>
                </div>
              )}
              {invoice.lastReminderSentAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-surface-500">{t('detail.lastReminderSentAt')}</span>
                  <span className="tabular text-surface-800">
                    {new Date(invoice.lastReminderSentAt).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' })}
                  </span>
                </div>
              )}
              {invoice.sepayTxnId && (
                <>
                  <Separator className="my-2" />
                  <div className="flex flex-col gap-0.5 text-sm">
                    <span className="text-surface-500">{t('detail.sepayTxn')}</span>
                    <span className="break-all font-mono text-xs text-surface-700">{invoice.sepayTxnId}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Items Column */}
        <div className="space-y-6 md:col-span-2">
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle className="text-surface-900">{t('detail.lineItems')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="hidden justify-between px-2 text-[11px] font-semibold uppercase tracking-wider text-surface-400 md:flex">
                <span className="flex-1">{t('detail.description')}</span>
                <span className="w-16 text-center">{t('detail.qty')}</span>
                <span className="w-32 text-right">{t('detail.unitPrice')}</span>
                <span className="w-32 text-right">{t('detail.total')}</span>
              </div>
              <Separator className="my-3 hidden md:block" />

              <div className="hidden space-y-1 md:block">
                {invoice.items?.map((item) => (
                  <div key={item.id} className="flex justify-between rounded-sm px-2 py-1.5 text-sm hover:bg-surface-50">
                    <div className="flex-1 pr-3">
                      <span className="text-surface-800">{item.description}</span>
                      {item.detailNote && (
                        <p className="mt-1 text-sm leading-relaxed text-surface-500">{item.detailNote}</p>
                      )}
                    </div>
                    <span className="tabular w-16 text-center text-surface-600">{item.quantity}</span>
                    <span className="tabular w-32 text-right text-surface-600">{formatCurrency(item.unitPrice)}</span>
                    <span className="tabular w-32 text-right font-medium text-surface-900">{formatCurrency(item.totalPrice)}</span>
                  </div>
                ))}
              </div>

              {/* Phones: one line per item, description left, money right — a
                  four-column table at 390px is four unreadable columns. Same
                  data, same order; the desktop table above is untouched. */}
              <div className="space-y-2 md:hidden">
                {invoice.items?.map((item) => (
                  <div key={item.id} className="flex items-start justify-between gap-3 text-[13.5px]">
                    <div className="min-w-0">
                      <span className="text-surface-600">{item.description}</span>
                      {/* qty × price only when it says something "1 ×" doesn't. */}
                      {item.quantity !== 1 && (
                        <span className="tabular ml-1.5 whitespace-nowrap text-surface-400">
                          {item.quantity} × {formatCurrency(item.unitPrice)}
                        </span>
                      )}
                      {item.detailNote && (
                        <p className="mt-0.5 text-[12px] leading-relaxed text-surface-500">{item.detailNote}</p>
                      )}
                    </div>
                    {/* A bù trừ line is money coming BACK — the same minus sign
                        and brand green the "Đã đặt cọc" row below already uses,
                        so the two read as one idea. U+2212, not a hyphen. */}
                    <span
                      className={cn(
                        'tabular shrink-0 font-medium',
                        item.totalPrice < 0 ? 'text-brand-700' : 'text-surface-900',
                      )}
                    >
                      {item.totalPrice < 0
                        ? `−${formatCurrency(Math.abs(item.totalPrice))}`
                        : formatCurrency(item.totalPrice)}
                    </span>
                  </div>
                ))}
              </div>

              <Separator className="my-4" />

              <div className="ml-auto max-w-xs space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-surface-500">{t('detail.subtotal')}</span>
                  <span className="tabular text-surface-800">{formatCurrency(invoice.subTotal)}</span>
                </div>
                {invoice.taxRate > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-surface-500">{t('detail.tax', { rate: invoice.taxRate })}</span>
                    <span className="tabular text-surface-800">{formatCurrency(invoice.totalAmount - invoice.subTotal)}</span>
                  </div>
                )}
                {invoice.depositAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-surface-500">{t('detail.depositPaid')}</span>
                    <span className="tabular text-brand-700">−{formatCurrency(invoice.depositAmount)}</span>
                  </div>
                )}
                <div className="mt-1 flex items-baseline justify-between border-t border-surface-200 pt-3">
                  {/* This line is ARITHMETIC, not live state (the quick card owns
                      Còn lại/Tổng by status — Johnny 2026-09-01): with no deposit
                      it is simply the invoice total; with a deposit row above it,
                      it genuinely is what remains after the deposit, so only then
                      does it keep the "Còn lại" name. */}
                  <span className="text-sm font-semibold text-surface-700">
                    {t(invoice.depositAmount > 0 ? 'detail.balanceDue' : 'detail.grandTotal')}
                  </span>
                  <span className="tabular text-xl font-semibold text-surface-900">{formatCurrency(balanceDue)}</span>
                </div>
                {/* Desktop only — phones carry this under the QR itself. It is
                    the answer to "do I have to come back and tick something
                    when they pay?", which is worth saying wherever the amount
                    due is. */}
                {payable && !nothingToCollect && (
                  <p className="hidden pt-2 text-[12px] leading-relaxed text-surface-500 md:block">
                    {t('detail.qrConsequence')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {(invoice.notes || invoice.absenceChargeNotice) && (
            <Card className="shadow-none">
              <CardHeader>
                <Label>{t('detail.notes')}</Label>
              </CardHeader>
              <CardContent className="space-y-2">
                {invoice.notes && (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-surface-700">{invoice.notes}</p>
                )}
                {/* Frozen at generation time — see Invoice.absenceChargeNotice's doc
                    comment. Shown alongside the invoice's own notes, never folded
                    into the editable `notes` field so an edit can't delete it. */}
                {invoice.absenceChargeNotice && (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-surface-700">{invoice.absenceChargeNotice}</p>
                )}
              </CardContent>
            </Card>
          )}

          <ReviewTargetsCard targets={invoice.reviewTargets} />
        </div>
      </div>

      <SendInvoiceDialog invoice={invoice} mode="send" open={sendOpen} onOpenChange={setSendOpen} />
      <SendInvoiceDialog invoice={invoice} mode="reminder" open={reminderOpen} onOpenChange={setReminderOpen} />
      <RecordPaymentDrawer
        open={recordOpen}
        onOpenChange={setRecordOpen}
        mode={{
          kind: 'invoice',
          invoice: { id: invoice.id, invoiceCode: invoice.invoiceCode },
          defaultAmount: Math.max(0, Number(invoice.totalAmount) - Number(invoice.paidAmount ?? 0)),
        }}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.delete')}</AlertDialogTitle>
            <AlertDialogDescription>{t('detail.deleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('form.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('detail.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
